import { actionTypes as FirebaseActions } from 'react-redux-firebase';
import { call, delay, put, race, select, take, takeEvery, takeLatest } from 'redux-saga/effects';

import { monolithApiUrl } from '../../config/app';
import { DateFormatsEnum } from '../../constants/date';
import {
  getRunAnonymizationCheckCompletedMessage,
  RUN_ANONYMIZATION_CHECK_FAILED_MESSAGE,
  RUN_ANONYMIZATION_CHECK_MAX_CONSECUTIVE_FAILURES,
  RUN_ANONYMIZATION_CHECK_NOTIFICATION_ID,
  RUN_ANONYMIZATION_CHECK_POLL_INTERVAL_MS,
  RUN_ANONYMIZATION_CHECK_START_ERROR_MESSAGE,
  RUN_ANONYMIZATION_CHECK_STATUS_UNAVAILABLE_MESSAGE,
  RUN_ANONYMIZATION_CHECK_TIMED_OUT_MESSAGE,
} from '../../constants/organizationsKeys';
import { endpoints, queryParams } from '../../constants/routing';
import { IResponse } from '../../types/http';
import {
  AnonymizationRunStatusEnum,
  IAddOrganizationActionPayload,
  IAddOrganizationActionResponse,
  IAddOrganizationDepartmentActionPayload,
  IAnonymizationLastRunStatus,
  IDepartment,
  IDepartmentBudgetSummariesResponse,
  IEditOrganizationActionPayload,
  IEditOrganizationDepartmentActionPayload,
  IFetchOrganizationRequestActionPayload,
  IFetchOrganizationSuccessActionPayload,
  IOrganizationItem,
  IOrganizationListItem,
  IRemovePIIRequestPayload,
  IResumeAnonymizationCheckPayload,
  IRunAnonymizationCheckRequestPayload,
  OrganizationsRequestResponseDataTypeEnum,
} from '../../types/organizations';
import { IReduxAction } from '../../types/redux';
import { IApiError } from '../../types/shell';
import { formatDate } from '../../utils/date';
import notification from '../../utils/notification';
import {
  getAnonymizationLastRunStatus,
  getRemainingAnonymizationPollMs,
  isNewerAnonymizationRun,
} from '../../utils/organizations';
import { buildEndpointRoute } from '../../utils/routing';
import {
  clearPendingAnonymizationCheck,
  savePendingAnonymizationCheck,
} from '../../utils/runAnonymizationCheckStorage';
import * as AuthActions from '../actions/auth';
import * as OrganizationsActions from '../actions/organizations';
import * as ShellActions from '../actions/shell';
import { selectOrganizationById } from '../selectors/organizations';
import { AuthRequest } from './helpers';

const SESSION_ENDED_ACTIONS = [FirebaseActions.LOGOUT, FirebaseActions.AUTH_EMPTY_CHANGE];

export function* getOrganizationsWorkerSaga(
  action: IReduxAction<IFetchOrganizationRequestActionPayload>,
): Generator<any, any, any> {
  try {
    const { responseDataType } = action.payload || {};
    const endpoint = `${monolithApiUrl}${endpoints.getOrganizations}`;
    const qs = {
      ...(responseDataType ? { [queryParams.responseData]: responseDataType } : {}),
    };
    const response = yield call(AuthRequest, { queryParams: qs, endpoint });

    if (response.ok) {
      yield put(OrganizationsActions.fetchOrganizationsSuccess(response.body, { responseDataType }));
    } else {
      yield put(OrganizationsActions.fetchOrganizationsFailure(response.body));
    }
  } catch (error) {
    yield put(OrganizationsActions.fetchOrganizationsFailure(error));
  }
}

export function* addOrganizationWorkerSaga(
  action: IReduxAction<IAddOrganizationActionPayload>,
): Generator<any, any, any> {
  const { values, resolve, reject } = action.payload!;

  const {
    // org_options should go at the root level
    org_options,
    // all the rest should go under `organization` field name
    org_customization_options,
    ...organization
  } = values;

  try {
    const endpoint = `${monolithApiUrl}${endpoints.createOrg}`;
    const response = yield call(AuthRequest, {
      endpoint,
      method: 'POST',
      body: JSON.stringify({ organization, org_options, org_customization_options }),
    });

    if (response.ok) {
      const { org_id: uid, experience_id } = response.body;
      const createdOrganization = {
        ...values,
        uid,
        ...(experience_id ? { org_options: { ...values.org_options, experience_id } } : {}),
      };

      if (typeof resolve === 'function') {
        resolve(createdOrganization);
      }
      yield put(OrganizationsActions.addOrganizationSuccess(createdOrganization));
    } else {
      if (typeof reject === 'function') {
        reject(response.body);
      }
      yield put(OrganizationsActions.addOrganizationFailure(response.body));
    }
  } catch (error) {
    if (typeof reject === 'function') {
      reject(error);
    }
    yield put(OrganizationsActions.addOrganizationFailure(error));
  }
}

export function* editOrganizationWorkerSaga(
  action: IReduxAction<IEditOrganizationActionPayload>,
): Generator<any, any, any> {
  const { values, metadata, resolve, reject } = action.payload!;
  const { shouldReauthorize = false } = metadata! || {};

  const {
    // org_options should go at the root level
    org_options,
    // the fields below shouldn't be sent to the endpoint
    salesforce_client_secret_has_value,
    // rybbon_client_secret_has_value - we support removing value for this field, so it should always be sent to the endpoint
    // all the rest should go under `organization` field name
    org_customization_options,
    ...organization
  } = values;

  try {
    if (!organization.uid) {
      throw new Error('Organization ID is required');
    }
    const endpoint = `${monolithApiUrl}${buildEndpointRoute(endpoints.editOrg, { id: organization.uid })}`;

    const response = yield call(AuthRequest, {
      endpoint,
      method: 'PATCH',
      body: JSON.stringify({ organization, org_options, org_customization_options }),
    });

    if (response.ok) {
      if (typeof resolve === 'function') {
        resolve(values);
      }

      yield put(OrganizationsActions.editOrganizationSuccess(values as IOrganizationItem));
      if (shouldReauthorize) {
        yield put(AuthActions.authorizeUserRequest());
      }
      yield put(ShellActions.fetchThemeRequest());

      // recheck what response returning, maybe we should pass `request.organization` there and update org list in reducer.
    } else {
      if (typeof reject === 'function') {
        reject(response.body);
      }
      yield put(OrganizationsActions.editOrganizationFailure(response.body));
    }
  } catch (error) {
    if (typeof reject === 'function') {
      reject(error);
    }
    yield put(OrganizationsActions.editOrganizationFailure(error));
  }
}

export function* addOrganizationDepartmentWorkerSaga(action: IReduxAction<IAddOrganizationDepartmentActionPayload>) {
  const { department, resolve, reject } = action.payload!;

  try {
    const endpoint = `${monolithApiUrl}${endpoints.createOrgDepartment}`;
    const response: IResponse<IAddOrganizationActionResponse | IApiError> = yield call(AuthRequest, {
      endpoint,
      method: 'POST',
      body: JSON.stringify({ department }),
    });

    if (response.ok) {
      // Response body has only id of the created org department
      const { id: uid } = response.body as IAddOrganizationActionResponse;

      // backend protects us <3
      const createdDep: IDepartment = (({ available_budget: availableBudget, ...restDepProp }) => {
        return {
          ...restDepProp,
          uid,
          available_budget: typeof availableBudget === 'undefined' ? 0 : availableBudget,
          used_budget: 0,
          created_date: formatDate(new Date().toString(), DateFormatsEnum.Full),
        };
      })(department);

      if (typeof resolve === 'function') {
        resolve(createdDep);
      }
      yield put(OrganizationsActions.addOrganizationDepartmentSuccess(createdDep));
    } else {
      if (typeof reject === 'function') {
        reject(response.body);
      }
      yield put(OrganizationsActions.addOrganizationDepartmentFailure(response.body));
    }
  } catch (error) {
    if (typeof reject === 'function') {
      reject(error);
    }
    yield put(OrganizationsActions.addOrganizationDepartmentFailure(error));
  }
}

export function* editOrganizationDepartmentWorkerSaga(
  action: IReduxAction<IEditOrganizationDepartmentActionPayload>,
): Generator<any, any, any> {
  const { department, resolve, reject } = action.payload!;

  try {
    const endpoint = `${monolithApiUrl}${endpoints.editOrgDepartment}`;
    const response = yield call(AuthRequest, {
      endpoint,
      method: 'PATCH',
      body: (() => {
        const { used_budget, ...depProps } = department;

        return JSON.stringify({
          department: depProps,
        });
      })(),
    });

    if (response.ok) {
      if (typeof resolve === 'function') {
        resolve(department);
      }
      yield put(OrganizationsActions.editOrganizationDepartmentSuccess(department));
    } else {
      if (typeof reject === 'function') {
        reject(response.body);
      }
      yield put(OrganizationsActions.editOrganizationDepartmentFailure(response.body));
    }
  } catch (error) {
    if (typeof reject === 'function') {
      reject(error);
    }
    yield put(OrganizationsActions.editOrganizationDepartmentFailure(error));
  }
}

export function* departmentBudgetSummariesSaga() {
  try {
    const endpoint = `${monolithApiUrl}${endpoints.getDepartmentBudgetSummaries}`;

    const response: IResponse<IDepartmentBudgetSummariesResponse | IApiError> = yield call(AuthRequest, { endpoint });

    if (!response.ok) {
      yield put(OrganizationsActions.getDepartmentBudgetSummariesFailure(response.body as IApiError));
    } else {
      yield put(
        OrganizationsActions.getDepartmentBudgetSummariesSuccess(response.body as IDepartmentBudgetSummariesResponse),
      );
    }
  } catch (e) {
    yield put(OrganizationsActions.getDepartmentBudgetSummariesFailure(e));
  }
}

export function* removePIIFromOrganizationWorkerSaga(
  action: IReduxAction<IRemovePIIRequestPayload>,
): Generator<any, any, any> {
  const { orgId, from, to, resolve, reject } = action.payload!;

  try {
    const endpoint = `${monolithApiUrl}${endpoints.handlePIIForOrganization}`;
    const response = yield call(AuthRequest, {
      endpoint,
      method: 'POST',
      body: JSON.stringify({
        org_id: orgId,
        date_range: {
          from,
          to,
        },
      }),
    });

    if (response.ok) {
      if (typeof resolve === 'function') {
        resolve();
      }
      yield put(OrganizationsActions.removePIISuccess());
    } else {
      if (typeof reject === 'function') {
        const { message } = response.body || {};
        reject(message);
      }

      yield put(OrganizationsActions.removePIIFailure(response.body));
    }
  } catch (error) {
    if (typeof reject === 'function') {
      reject(error);
    }
    yield put(OrganizationsActions.removePIIFailure(error));
  }
}

interface IPolledAnonymizationRun {
  lastRunStatus: IAnonymizationLastRunStatus;
  organizations: IOrganizationListItem[];
}

export function* pollAnonymizationLastRunStatusSaga(
  orgId: string,
  baselineTimestamp: string | null,
  checkImmediately = false,
): Generator<any, IPolledAnonymizationRun | null, any> {
  let shouldWait = !checkImmediately;
  let consecutiveFailures = 0;

  while (consecutiveFailures < RUN_ANONYMIZATION_CHECK_MAX_CONSECUTIVE_FAILURES) {
    if (shouldWait) {
      yield delay(RUN_ANONYMIZATION_CHECK_POLL_INTERVAL_MS);
    }

    shouldWait = true;

    let response: IResponse<IFetchOrganizationSuccessActionPayload> | null = null;

    try {
      response = yield call(AuthRequest, {
        endpoint: `${monolithApiUrl}${endpoints.getOrganizations}`,
        queryParams: { [queryParams.responseData]: OrganizationsRequestResponseDataTypeEnum.All },
      });
    } catch (error) {
      response = null;
    }

    if (!response?.ok) {
      consecutiveFailures += 1;
      continue;
    }

    consecutiveFailures = 0;

    const organization = response.body?.organizations?.find(({ uid }) => uid === orgId);
    const lastRunStatus = getAnonymizationLastRunStatus(organization);

    if (isNewerAnonymizationRun(lastRunStatus, baselineTimestamp)) {
      return { lastRunStatus, organizations: response.body?.organizations ?? [] };
    }
  }

  return null;
}

export function* awaitAnonymizationCheckResultSaga(
  orgId: string,
  baselineTimestamp: string | null,
  startedAt: string,
  checkImmediately = false,
): Generator<any, void, any> {
  const remainingMs = getRemainingAnonymizationPollMs(startedAt, Date.now());
  const { polled }: { polled?: IPolledAnonymizationRun | null } =
    remainingMs > 0
      ? yield race({
          polled: call(pollAnonymizationLastRunStatusSaga, orgId, baselineTimestamp, checkImmediately),
          timedOut: delay(remainingMs),
        })
      : {};

  yield call(clearPendingAnonymizationCheck);

  if (!polled) {
    const isStatusUnavailable = polled === null;

    yield call(notification.info, RUN_ANONYMIZATION_CHECK_NOTIFICATION_ID, {
      content: isStatusUnavailable
        ? RUN_ANONYMIZATION_CHECK_STATUS_UNAVAILABLE_MESSAGE
        : RUN_ANONYMIZATION_CHECK_TIMED_OUT_MESSAGE,
    });
    yield put(OrganizationsActions.runAnonymizationCheckFinished());
    return;
  }

  const { lastRunStatus, organizations } = polled;
  const loadedOrganization: IOrganizationItem | undefined = yield select(selectOrganizationById(orgId));

  if (!loadedOrganization) {
    yield put(
      OrganizationsActions.fetchOrganizationsSuccess(
        { organizations },
        { responseDataType: OrganizationsRequestResponseDataTypeEnum.All },
      ),
    );
  }

  yield put(OrganizationsActions.updateAnonymizationLastRunStatus({ orgId, lastRunStatus }));

  if (lastRunStatus.status === AnonymizationRunStatusEnum.Completed) {
    yield call(notification.success, RUN_ANONYMIZATION_CHECK_NOTIFICATION_ID, {
      content: getRunAnonymizationCheckCompletedMessage(lastRunStatus.number_sends),
    });
  } else {
    yield call(notification.error, RUN_ANONYMIZATION_CHECK_NOTIFICATION_ID, {
      content: lastRunStatus.error_message || RUN_ANONYMIZATION_CHECK_FAILED_MESSAGE,
    });
  }

  yield put(OrganizationsActions.runAnonymizationCheckFinished());
}

export function* runAnonymizationCheckFlowSaga({
  orgId,
  from,
  to,
}: IRunAnonymizationCheckRequestPayload): Generator<any, void, any> {
  const savedOrganization: IOrganizationItem | undefined = yield select(selectOrganizationById(orgId));
  const baselineTimestamp = getAnonymizationLastRunStatus(savedOrganization)?.timestamp ?? null;

  try {
    const response = yield call(AuthRequest, {
      endpoint: `${monolithApiUrl}${endpoints.handlePIIForOrganization}`,
      method: 'POST',
      body: JSON.stringify({ org_id: orgId, date_range: { from, to } }),
    });

    if (!response.ok) {
      const message = response.body?.message;

      yield call(notification.error, RUN_ANONYMIZATION_CHECK_NOTIFICATION_ID, {
        content: typeof message === 'string' && message ? message : RUN_ANONYMIZATION_CHECK_START_ERROR_MESSAGE,
      });
      yield put(OrganizationsActions.runAnonymizationCheckFinished());
      return;
    }
  } catch (error) {
    yield call(notification.error, RUN_ANONYMIZATION_CHECK_NOTIFICATION_ID, {
      content: RUN_ANONYMIZATION_CHECK_START_ERROR_MESSAGE,
    });
    yield put(OrganizationsActions.runAnonymizationCheckFinished());
    return;
  }

  const startedAt = new Date().toISOString();

  yield call(savePendingAnonymizationCheck, { orgId, baselineTimestamp, startedAt });

  yield put(OrganizationsActions.runAnonymizationCheckRunning());
  yield call(awaitAnonymizationCheckResultSaga, orgId, baselineTimestamp, startedAt);
}

export function* runAnonymizationCheckWorkerSaga(
  action: IReduxAction<IRunAnonymizationCheckRequestPayload>,
): Generator<any, void, any> {
  yield race({
    flow: call(runAnonymizationCheckFlowSaga, action.payload!),
    cancel: take(OrganizationsActions.RUN_ANONYMIZATION_CHECK_CANCEL),
    sessionEnded: take(SESSION_ENDED_ACTIONS),
  });
}

export function* resumeAnonymizationCheckWorkerSaga(
  action: IReduxAction<IResumeAnonymizationCheckPayload>,
): Generator<any, void, any> {
  const { orgId, baselineTimestamp, startedAt } = action.payload!;

  yield race({
    flow: call(awaitAnonymizationCheckResultSaga, orgId, baselineTimestamp, startedAt, true),
    cancel: take(OrganizationsActions.RUN_ANONYMIZATION_CHECK_CANCEL),
    sessionEnded: take(SESSION_ENDED_ACTIONS),
  });
}

export function* clearPendingAnonymizationCheckOnLogoutSaga(): Generator<any, void, any> {
  yield call(clearPendingAnonymizationCheck);
}

const sagas = {
  *watchOrganizationsWatcher() {
    yield takeLatest(OrganizationsActions.FETCH_ORGANIZATIONS_REQUEST, getOrganizationsWorkerSaga);
  },
  *watchAddOrganizationWatcher() {
    yield takeLatest(OrganizationsActions.ADD_ORGANIZATION_REQUEST, addOrganizationWorkerSaga);
  },
  *watchEditOrganizationWatcher() {
    yield takeLatest(OrganizationsActions.EDIT_ORGANIZATION_REQUEST, editOrganizationWorkerSaga);
  },
  *watchAddOrganizationDepartmentWatcher() {
    yield takeLatest(OrganizationsActions.ADD_ORGANIZATION_DEPARTMENT_REQUEST, addOrganizationDepartmentWorkerSaga);
  },
  *watchEditOrganizationDepartmentWatcher() {
    yield takeLatest(OrganizationsActions.EDIT_ORGANIZATION_DEPARTMENT_REQUEST, editOrganizationDepartmentWorkerSaga);
  },
  *watchGetDepartmentBudgetSummariesWatcher() {
    yield takeLatest(OrganizationsActions.GET_DEPARTMENT_BUDGET_SUMMARIES_REQUEST, departmentBudgetSummariesSaga);
  },
  *watchRemovePIIForOrganization() {
    yield takeLatest(OrganizationsActions.REMOVE_PII_REQUEST, removePIIFromOrganizationWorkerSaga);
  },
  *watchRunAnonymizationCheck() {
    yield takeLatest(OrganizationsActions.RUN_ANONYMIZATION_CHECK_REQUEST, runAnonymizationCheckWorkerSaga);
  },
  *watchResumeAnonymizationCheck() {
    yield takeLatest(OrganizationsActions.RESUME_ANONYMIZATION_CHECK, resumeAnonymizationCheckWorkerSaga);
  },
  *watchClearPendingAnonymizationCheckOnLogout() {
    yield takeEvery(SESSION_ENDED_ACTIONS, clearPendingAnonymizationCheckOnLogoutSaga);
  },
};

export default sagas;
